import type { SearchHit } from '@contracts';
import { formatSupportId } from '@api/devices/identity-contracts';
import { CopyButton } from '../ui/misc';

/** Approval targets keep mutable text away from the complete, independently readable action identifier. */
export function ApprovalDeviceIdentity({ device }: { device: { id: string; title: string; hostname?: string | null } }) {
  return <span className="block min-w-0 whitespace-normal">
    <bdi dir="auto" className="block break-words font-medium">{device.title}</bdi>
    {device.hostname && device.hostname !== device.title && <span className="block text-xs text-ink-3">Hostname: <bdi dir="auto">{device.hostname}</bdi></span>}
    <span className="block text-xs text-ink-3">Device ID</span>
    <bdi dir="ltr" className="block break-all whitespace-normal font-mono text-xs">{device.id}</bdi>
  </span>;
}

/** Names, support IDs and short UUIDs help identify a device; actions always use its full UUID. */
export function DeviceIdentity({ device }: { device: Pick<SearchHit, 'id' | 'title' | 'sameNameCount' | 'hostname' | 'displayName' | 'supportId'> }) {
  const shortId = device.id.slice(0, 8).toLowerCase();
  return <span className="block text-[12px] text-ink-3" onClick={e => e.stopPropagation()}
    onDoubleClick={e => e.stopPropagation()} onKeyDown={e => {
      // Copy-button activation must not activate its result row; global search/navigation keys still bubble.
      if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
    }}>
    {device.displayName && device.hostname && <span className="block font-mono">{device.hostname}</span>}
    {device.supportId && <span className="mr-2 inline-flex items-center gap-1">
      Support ID <span className="font-mono">{formatSupportId(device.supportId)}</span>
      <CopyButton value={formatSupportId(device.supportId)} label="Copy support ID" />
    </span>}
    <span className="inline-flex items-center gap-1" title={`Device ID: ${device.id}`}>
      <span>ID</span><span className="font-mono">{shortId}</span>
      <CopyButton value={shortId} label={`Copy device ID ${shortId}`} />
    </span>
    {(device.sameNameCount ?? 0) > 1 && <span className="block text-attention">
      {device.sameNameCount} devices named {device.hostname ?? device.title} in this customer
    </span>}
  </span>;
}
